// Camera pose calibration (Phase 4). Position (m), rotation (deg, pitch /
// yaw / roll), vertical field of view (deg). Edited with sliders in the debug
// overlay, persisted to localStorage on every change, exported as JSON to be
// committed as src/data/calibration.json, which is the fallback when
// localStorage is empty.
import * as THREE from 'three';
import { config } from '../config.js';
import { storage } from '../util/storage.js';
import { el, button, slider } from '../debug/controls.js';
import fileDefault from '../data/calibration.json';
import { saveToRepoButton } from '../debug/saveToRepo.js';

const KEY = 'mw.calibration.v1';

function fromFile() {
  return { position: [...fileDefault.position], rotation: [...fileDefault.rotation], fov: fileDefault.fov };
}

export function createCalibration({ camera, overlay, log = () => {} }) {
  const saved = storage.get(KEY);
  const state = saved?.position && saved?.rotation && saved?.fov ? saved : fromFile();
  let fine = false;
  const steps = () => (fine ? config.calibration.stepFine : config.calibration.stepCoarse);

  function apply() {
    camera.position.fromArray(state.position);
    const [pitch, yaw, roll] = state.rotation.map(THREE.MathUtils.degToRad);
    camera.rotation.set(pitch, yaw, roll, 'YXZ');
    camera.fov = state.fov;
    camera.updateProjectionMatrix();
  }

  function save() {
    storage.set(KEY, state);
  }

  function toJSON() {
    return JSON.stringify(
      {
        _comment: fileDefault._comment,
        position: state.position.map((v) => Number(v.toFixed(4))),
        rotation: state.rotation.map((v) => Number(v.toFixed(2))),
        fov: Number(state.fov.toFixed(2)),
      },
      null,
      2,
    );
  }

  // Overlay panel
  const section = overlay.section('Calibration');
  const sliders = [];
  const r = config.calibration.range;
  const make = (label, min, max, get, set, stepKey, decimals) => {
    const s = slider({
      label,
      min,
      max,
      step: () => steps()[stepKey],
      value: get(),
      decimals,
      onChange: (v) => {
        set(v);
        apply();
        save();
      },
    });
    sliders.push(s);
    section.append(s.root);
    return s;
  };
  const axisSliders = ['x', 'y', 'z'].map((axis, i) =>
    make(`position ${axis} (m)`, -r.position, r.position, () => state.position[i], (v) => (state.position[i] = v), 'position', 3),
  );
  const rotSliders = ['pitch', 'yaw', 'roll'].map((name, i) =>
    make(`${name} (deg)`, -r.rotation, r.rotation, () => state.rotation[i], (v) => (state.rotation[i] = v), 'rotation', 1),
  );
  const fovSlider = make('vertical fov (deg)', r.fovMin, r.fovMax, () => state.fov, (v) => (state.fov = v), 'fov', 1);

  const refreshSliders = () => {
    axisSliders.forEach((s, i) => s.set(state.position[i]));
    rotSliders.forEach((s, i) => s.set(state.rotation[i]));
    fovSlider.set(state.fov);
    sliders.forEach((s) => s.refreshStep());
  };

  const exportBox = el('textarea', { readonly: '', spellcheck: 'false' });
  exportBox.hidden = true;
  const fineButton = button('Fine steps', () => {
    fine = !fine;
    fineButton.classList.toggle('on', fine);
    sliders.forEach((s) => s.refreshStep());
  });
  const bar = el('div', { class: 'mw-bar' }, [
    fineButton,
    button('Export JSON', async () => {
      exportBox.value = toJSON();
      exportBox.hidden = false;
      try {
        await navigator.clipboard.writeText(exportBox.value);
        log('calibration JSON copied to clipboard');
      } catch {
        log('clipboard unavailable; select the text in the box');
      }
    }),
    button('Reset to file', () => {
      Object.assign(state, fromFile());
      apply();
      save();
      refreshSliders();
      log('calibration reset to src/data/calibration.json');
    }),
  ]);
  const saveButton = saveToRepoButton('calibration', toJSON, log);
  if (saveButton) bar.append(saveButton);
  section.querySelector('h2').after(bar);
  section.append(exportBox);

  overlay.row('Status', 'Calibration', () =>
    `p ${state.position.map((v) => v.toFixed(3)).join(' ')} | r ${state.rotation.map((v) => v.toFixed(1)).join(' ')} | fov ${state.fov.toFixed(1)}${saved ? '' : ' (file default)'}`,
  );

  apply();
  return { state, apply, toJSON, refreshSliders };
}
